import { ArrowLeft, Moon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconButton, Switch, Tabs, TabsList, TabsTrigger } from '@/components/ui';
import { useTheme } from '@/theme/useTheme';
import { NodeOverview } from './NodeOverview';
import { ShowcaseShell } from './ShowcaseShell';
import './showcase.css';

/*
  /design/showcase — витрина направления v3: один реалистичный экран из всех типов данных,
  собранный только из системы, с переключателями для решения глазами: палитра, режим пульта,
  шрифты, плотность, тема. Значения переключателей — в showcase.css.
*/

type Palette = 'slate' | 'warm';
type UiFont = 'inter' | 'onest';
type DisplayFont = 'none' | 'source-serif' | 'playfair';

function Segmented<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-2xs font-medium text-fg-subtle uppercase">{label}</span>
      <Tabs variant="pill" value={value} onValueChange={(v) => onChange(v as T)}>
        <TabsList>
          {options.map((o) => (
            <TabsTrigger key={o.value} value={o.value}>
              {o.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}

export function Showcase() {
  const { mode, toggle } = useTheme();
  const [palette, setPalette] = useState<Palette>('slate');
  const [consoleMode, setConsoleMode] = useState(false);
  const [uiFont, setUiFont] = useState<UiFont>('inter');
  const [displayFont, setDisplayFont] = useState<DisplayFont>('none');
  const [dense, setDense] = useState(false);

  // Шрифты-кандидаты грузятся только здесь, чтобы не утяжелять основной бандл.
  useEffect(() => {
    void import('@fontsource-variable/onest');
    void import('@fontsource-variable/source-serif-4');
    void import('@fontsource-variable/playfair-display');
  }, []);

  return (
    <div
      data-showcase=""
      data-palette={palette}
      data-ui-font={uiFont}
      data-display-font={displayFont}
      className="flex h-screen flex-col bg-frame text-fg"
    >
      <div className="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-2 border-b border-line bg-canvas px-4 py-2">
        <Link
          to="/design"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="size-3.5" />
          Галерея
        </Link>
        <span className="text-sm font-semibold text-fg">Витрина: узел host-338</span>
        <Segmented
          label="Палитра"
          value={palette}
          onChange={setPalette}
          options={[
            { value: 'slate', label: 'Slate (сейчас)' },
            { value: 'warm', label: 'Тёплая' },
          ]}
        />
        <Segmented
          label="Шрифт UI"
          value={uiFont}
          onChange={setUiFont}
          options={[
            { value: 'inter', label: 'Inter' },
            { value: 'onest', label: 'Onest' },
          ]}
        />
        <Segmented
          label="Заголовки"
          value={displayFont}
          onChange={setDisplayFont}
          options={[
            { value: 'none', label: 'Гротеск' },
            { value: 'source-serif', label: 'Source Serif 4' },
            { value: 'playfair', label: 'Playfair' },
          ]}
        />
        <Switch size="sm" checked={consoleMode} onCheckedChange={setConsoleMode} label="Режим пульта" />
        <Switch size="sm" checked={dense} onCheckedChange={setDense} label="Плотно" />
        <div className="ml-auto">
          <IconButton
            aria-label={mode === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
            variant="secondary"
            size="sm"
            onClick={toggle}
          >
            {mode === 'dark' ? <Sun /> : <Moon />}
          </IconButton>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <ShowcaseShell consoleMode={consoleMode}>
          <NodeOverview dense={dense} />
        </ShowcaseShell>
      </div>
    </div>
  );
}
